export type FormatValue = string | number;

export type FundamentalFormatter = (value: FormatValue) => string;

// Every registered fundamental format kind name (issue #529) -- keeps `Format`'s own
// keys, and any `FieldFormat` entry that names one of these kinds (e.g. "course" on
// `LogViewData.fieldFormat`), checked against a known, closed set instead of an
// arbitrary string, so a typo'd kind name is a compile error rather than a silently
// missing formatter at runtime.
export type FormatKind =
  | "angle" | "course" | "date" | "dateCompact" | "dateAndTime" | "dateInYear"
  | "dateTime" | "durationHours" | "distance" | "depth" | "gps-lat" | "gps-lng"
  | "meters" | "percentage" | "pluralize" | "pressure" | "rpm" | "speed"
  | "temperature" | "text" | "time" | "timeh" | "timeUtc" | "timezone" | "timeAgoDate";

// A single field's formatter (issue #529 EditHistory/input-field formatting parity):
// either the name of a registered fundamental format kind -- dispatched through
// `format`/`app.format` via `applyFieldFormatEntry` below, for a field whose value is
// a plain `FormatValue` (a number or string, e.g. "course"/"speed") -- or a
// `CompositeFormatter`, for a field whose value isn't (an array, an id needing an
// external lookup, etc.) and/or needs extra context (a translation function, a
// lookup list) a bare kind name can't express.
export type FieldFormatEntry = FormatKind | CompositeFormatter;
export interface FieldFormat {
  [field: string]: FieldFormatEntry;
}

export type CompositeFormatter = (value: unknown) => string;

// Wraps a strongly-typed formatter body (operating on a precise value type `T`) as a
// `CompositeFormatter` -- runtime-guards `unknown` down to `T` first, rendering "–"
// if the guard fails or the value is undefined/null (matching `FieldFormat`'s shared
// dash convention for a missing value), so the formatter body itself never needs an
// `unknown`/type-assertion escape.
export function compositeField<T>(guard: (value: unknown) => value is T, formatter: (value: T) => string): CompositeFormatter {
  return (value) => (value == undefined || !guard(value) ? "–" : formatter(value));
}

// Resolves one `FieldFormatEntry` against a value (issue #529): a `FormatKind`
// string dispatches through `formatFn` (typically `format` itself, or an
// `IAppState.format` bound method), rendering "–" for a missing value rather than
// `format`'s own "" -- matching `EditHistory`'s `formatEditValue` dash convention. A
// `CompositeFormatter` is called directly; it already handles its own missing-value
// case (see `compositeField`).
export function applyFieldFormatEntry(entry: FieldFormatEntry, value: unknown, formatFn: (kind: string, value?: FormatValue) => string): string {
  if (typeof entry === "string") {
    return value == undefined ? "–" : formatFn(entry, value as FormatValue);
  }
  return entry(value);
}

export function dateValue(value: FormatValue): Date {
  if (typeof value == "number") {
    return new Date(value);
  }
  return new Date(Date.parse(value));
}

export function intValue(value: FormatValue): number {
  if (typeof value == "number") {
    return value;
  }
  return parseInt(value);
}

export function floatValue(value: FormatValue): number {
  if (typeof value == "number") {
    return value;
  }
  return parseFloat(value);
}

export const referenceDate = Date.UTC(2001, 0, 1);
export const Format: Record<FormatKind, FundamentalFormatter> = {
  "angle": function (value: FormatValue): string {
    const angle = floatValue(value);
    if (isNaN(angle)) return "";
    return `${angle.toFixed(0)}°`;
  },
  "course": function (value: FormatValue): string {
    const course = floatValue(value);
    if (isNaN(course)) return "";
    return `${course.toFixed(0).padStart(3, "0")}°`;
  },
  "date": function (date: FormatValue): string {
    return dateValue(date).toLocaleDateString(navigator.language, { month: "long", day: "numeric", year: "numeric", timeZone: "UTC" });
  },
  "dateCompact": function (date: FormatValue): string {
    return dateValue(date).toLocaleDateString(navigator.language, { month: "short", day: "numeric", year: "numeric", timeZone: "UTC" });
  },
  "dateAndTime": function (date: FormatValue): string {
    return dateValue(date).toLocaleString(navigator.language, { dateStyle: "full", timeStyle: "short" });
  },
  "dateInYear": function (date: FormatValue): string {
    return dateValue(date).toLocaleDateString(navigator.language, { month: "numeric", day: "numeric", weekday: "short", timeZone: "UTC" });
  },
  "dateTime": function (date: FormatValue): string {
    return dateValue(date).toLocaleTimeString(navigator.language, { timeStyle: "short" });
  },
  "durationHours": function (value: FormatValue): string {
    const durationHours = intValue(value);
    if (isNaN(durationHours)) return "";
    return `${durationHours.toFixed(0)} h`;
  },
  "distance": function (value: FormatValue): string {
    const distance = floatValue(value);
    if (isNaN(distance)) return "";
    if (distance >= 1000) {
      return `${distance.toFixed(0)} nm`;
    }
    return `${distance.toFixed(1)} nm`;
  },
  "depth": function (value: FormatValue): string {
    const distance = floatValue(value);
    if (isNaN(distance)) return "";
    return `${distance.toFixed(1)} m`;
  },
  "gps-lat": function (value: FormatValue): string {
    const lat = floatValue(value);
    if (isNaN(lat)) return "";
    return `${(lat < 0 ? lat * -1 : lat).toFixed(3)}° ${lat >= 0 ? "N" : "S"}`;
  },
  "gps-lng": function (value: FormatValue): string {
    const lng = floatValue(value);
    if (isNaN(lng)) return "";
    return `${(lng < 0 ? lng * -1 : lng).toFixed(3)}° ${lng >= 0 ? "E" : "W"}`;
  },
  "meters": function (value: FormatValue): string {
    const meters = floatValue(value);
    if (isNaN(meters)) return "";
    return `${meters.toFixed(1)} m`;
  },
  "percentage": function (value: FormatValue): string {
    const percentage = floatValue(value);
    if (isNaN(percentage)) return "";
    return `${percentage.toFixed(0)} %`;
  },
  "pluralize": function (n: FormatValue): string {
    return intValue(n) != 1 ? "s" : "";
  },
  "pressure": function (n: FormatValue): string {
    const pressure = floatValue(n);
    if (isNaN(pressure)) return "";
    return `${Math.abs(pressure).toFixed(1)} hPa`;
  },
  "rpm": function (n: FormatValue): string {
    const rpm = floatValue(n);
    if (isNaN(rpm)) return "";
    return `${Math.abs(rpm).toFixed(0)} rpm`;
  },
  "speed": function (value: FormatValue): string {
    const speed = floatValue(value);
    if (isNaN(speed)) return "";
    return `${speed.toFixed(1)} kn`;
  },
  "temperature": function (value: FormatValue): string {
    const temperature = floatValue(value);
    if (isNaN(temperature)) return "";
    return `${temperature.toFixed(0)} °C`;
  },
  // Plain-string passthrough (issue #529) -- for fields (e.g. Trip/Cruise's `title`)
  // that need the shared "–"-for-missing convention but no unit/locale formatting.
  "text": function (value: FormatValue): string {
    return typeof value === "string" ? value : String(value);
  },
  "time": function (time: FormatValue): string {
    const hour = intValue(time) / 3600;
    return `${Math.floor(hour)}:${Math.floor(hour * 60 % 60).toFixed(0).padStart(2, "0")}`;
  },
  "timeh": function (time: FormatValue): string {
    const hour = intValue(time) / 3600;
    return `${Math.floor(hour)}:${Math.floor(hour * 60 % 60).toFixed(0).padStart(2, "0")} h`;
  },
  "timeUtc": function (time: FormatValue): string {
    const d = new Date(referenceDate + (intValue(time) * 1000));
    return `${d.getUTCHours()}:${d.getUTCMinutes().toFixed(0).padStart(2, "0")}`;
  },
  "timezone": function (timezone: FormatValue): string {
    const offset = intValue(timezone);
    if (offset == 0) {
      return "UTC";
    }
    // Format from the magnitude: formatting the signed offset used to floor
    // -9.5 h to -10 and drop the (negative) minutes, so -9:30 read as "UTC-10"
    // and -3:30 as "UTC-4" (#1821).
    const magnitude = Math.abs(offset);
    const hours = Math.floor(magnitude / 3600);
    const minutes = Math.round((magnitude % 3600) / 60);
    return `UTC${offset > 0 ? "+" : "-"}${hours}${minutes > 0 ? `:${minutes.toString().padStart(2, "0")}` : ""}`;
  },
  "timeAgoDate": function (date: FormatValue): string {
    const rtf1 = new Intl.RelativeTimeFormat("en", { style: "short" });
    const d = dateValue(date);
    let t = (new Date().valueOf() - d.valueOf()) / 1000;
    if (t < 60) {
      return rtf1.format(-Math.floor(t), "seconds");
    }
    t /= 60;
    if (t < 60) {
      return rtf1.format(-Math.floor(t), "minutes");
    }
    t /= 60;
    if (t < 24) {
      return rtf1.format(-Math.floor(t), "hours");
    }
    return Format.date(date);
  },
};

export function format(format: string, value?: FormatValue): string {
  if (value == undefined || value == null) {
    return "";
  }
  const formatter = Format[format as FormatKind];
  if (formatter) {
    return formatter(value);
  }
  return coerceFormatValue(value);
};
import { coerceFormatValue } from "app-base/runtime/format"
